import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
    MailCheck,
    Send,
    RefreshCw,
    Clock,
    CheckCircle2,
    AlertCircle,
    Loader2,
    Users,
    History,
    Database,
    Eye,
    X,
    ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

/**
 * WelcomeEmailsPanel — Panel admin para envío MANUAL de bienvenidas.
 *
 * Diseño:
 *  - Dos pestañas: Pendientes / Enviadas
 *  - Botón "Enviar bienvenida a pendientes" con barra de progreso
 *  - Botón "Reenviar" por fila en histórico
 *  - Resultado individual con éxito/fallo después de envío masivo
 */

function formatDate(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return d.toLocaleString('es-CL', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return String(iso).slice(0, 16);
    }
}

function ResultRow({ r }) {
    return (
        <div
            data-testid={`welcome-batch-result-${r.email}`}
            className={cn(
                'flex items-center justify-between gap-3 px-3 py-1.5 rounded text-xs border',
                r.ok
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                    : 'bg-rose-50 border-rose-200 text-rose-900'
            )}
        >
            <div className="flex items-center gap-2 min-w-0">
                {r.ok ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                ) : (
                    <AlertCircle className="h-3.5 w-3.5 text-rose-600 shrink-0" />
                )}
                <span className="truncate">{r.email || '(sin email)'}</span>
                {r.nombre && <span className="text-slate-500 truncate">· {r.nombre}</span>}
            </div>
            <span className="text-[10px] uppercase tracking-wide font-semibold">
                {r.ok ? 'Enviado' : r.reason || 'Error'}
            </span>
        </div>
    );
}

export default function WelcomeEmailsPanel() {
    const [tab, setTab] = useState('pending'); // 'pending' | 'sent'
    const [pending, setPending] = useState([]);
    const [sent, setSent] = useState([]);
    const [loadingPending, setLoadingPending] = useState(false);
    const [loadingSent, setLoadingSent] = useState(false);
    const [sending, setSending] = useState(false);
    const [resendingId, setResendingId] = useState(null);
    const [migrating, setMigrating] = useState(false);
    const [batchResults, setBatchResults] = useState(null);
    const [progress, setProgress] = useState(0);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewName, setPreviewName] = useState('');
    const [previewGenero, setPreviewGenero] = useState('');
    const [previewSrc, setPreviewSrc] = useState('');
    const [selectedIds, setSelectedIds] = useState(new Set()); // selección manual

    const loadPending = useCallback(async () => {
        setLoadingPending(true);
        try {
            const { data } = await api.get('/admin/welcome/pending');
            setPending(data.users || []);
        } catch (err) {
            toast.error('Error cargando pendientes: ' + (err?.response?.data?.detail || err.message));
        } finally {
            setLoadingPending(false);
        }
    }, []);

    const loadSent = useCallback(async () => {
        setLoadingSent(true);
        try {
            const { data } = await api.get('/admin/welcome/sent', { params: { limit: 500 } });
            setSent(data.users || []);
        } catch (err) {
            toast.error('Error cargando histórico: ' + (err?.response?.data?.detail || err.message));
        } finally {
            setLoadingSent(false);
        }
    }, []);

    useEffect(() => {
        loadPending();
        loadSent();
    }, [loadPending, loadSent]);

    // Simulación de progreso lineal mientras el batch corre en backend.
    // Cada usuario tarda ~200-400ms (envío + 100ms pausa). Estimamos 350ms/usuario.
    useEffect(() => {
        if (!sending) return;
        const total = pending.length || 1;
        const estMs = total * 350;
        const startedAt = Date.now();
        const interval = setInterval(() => {
            const elapsed = Date.now() - startedAt;
            const pct = Math.min(95, Math.round((elapsed / estMs) * 100));
            setProgress(pct);
        }, 250);
        return () => clearInterval(interval);
    }, [sending, pending.length]);

    const sendBatch = async () => {
        if (selectedIds.size === 0) {
            toast.error('Selecciona al menos un destinatario');
            return;
        }
        // Construir lista de nombres para la confirmación
        const selectedUsers = pending.filter((u) => selectedIds.has(u.id));
        const namesList = selectedUsers
            .slice(0, 10)
            .map((u) => `• ${u.nombre || u.email}`)
            .join('\n');
        const more = selectedUsers.length > 10 ? `\n…y ${selectedUsers.length - 10} más` : '';
        const confirmed = window.confirm(
            `¿Enviar bienvenida a ${selectedIds.size} docente(s) seleccionado(s)?\n\n` +
            `${namesList}${more}\n\n` +
            `Esta acción NO se puede deshacer.`
        );
        if (!confirmed) return;
        setSending(true);
        setProgress(2);
        setBatchResults(null);
        try {
            const { data } = await api.post('/admin/welcome/send-batch', {
                user_ids: Array.from(selectedIds),
            });
            setProgress(100);
            setBatchResults(data);
            if (data.fail === 0) {
                toast.success(`✅ ${data.ok} bienvenidas enviadas con éxito.`);
            } else {
                toast.warning(`Enviadas ${data.ok} · ${data.fail} fallaron — revisa el detalle abajo.`);
            }
            setSelectedIds(new Set());
            await Promise.all([loadPending(), loadSent()]);
        } catch (err) {
            toast.error('Error en envío masivo: ' + (err?.response?.data?.detail || err.message));
        } finally {
            setTimeout(() => {
                setSending(false);
                setProgress(0);
            }, 800);
        }
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === pending.length && pending.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(pending.map((u) => u.id)));
        }
    };

    const toggleOne = (id) => {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelectedIds(next);
    };

    const resendOne = async (userId, email) => {
        if (!window.confirm(`Reenviar bienvenida a ${email}?`)) return;
        setResendingId(userId);
        try {
            const { data } = await api.post('/admin/welcome/resend', { user_id: userId });
            if (data.ok) toast.success(`Reenviada a ${data.email}`);
            else toast.error(`Falló: ${data.reason || 'Resend rechazó el envío'}`);
            await Promise.all([loadPending(), loadSent()]);
        } catch (err) {
            toast.error('Error: ' + (err?.response?.data?.detail || err.message));
        } finally {
            setResendingId(null);
        }
    };

    const openPreview = async (name = '', gen = '') => {
        try {
            const params = {};
            if (name) params.nombre = name;
            if (gen) params.genero = gen;
            const { data } = await api.get('/admin/welcome/preview', {
                params: Object.keys(params).length ? params : undefined,
                responseType: 'text',
                transformResponse: [(r) => r],
            });
            setPreviewSrc(data);
            setPreviewName(name);
            setPreviewGenero(gen);
            setPreviewOpen(true);
        } catch (err) {
            toast.error('Error generando vista previa: ' + (err?.response?.data?.detail || err.message));
        }
    };

    const runMigration = async () => {
        if (
            !window.confirm(
                'Marcar a TODOS los usuarios existentes (legacy) como "bienvenida enviada".\n\n' +
                    'Esto evita reenviar correos a usuarios antiguos. Solo afecta a quienes no tienen el campo seteado. ¿Continuar?'
            )
        )
            return;
        setMigrating(true);
        try {
            const { data } = await api.post('/admin/welcome/migrate-existing');
            toast.success(`Migración OK · ${data.modified} usuarios marcados como ya enviados.`);
            await Promise.all([loadPending(), loadSent()]);
        } catch (err) {
            toast.error('Error en migración: ' + (err?.response?.data?.detail || err.message));
        } finally {
            setMigrating(false);
        }
    };

    return (
        <Card
            className="border-amber-200 bg-gradient-to-br from-amber-50/60 via-white to-white"
            data-testid="welcome-emails-panel"
        >
            <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <CardTitle className="flex items-center gap-2 font-heading text-base text-slate-900">
                            <MailCheck className="h-5 w-5 text-amber-700" />
                            Correos de Bienvenida
                        </CardTitle>
                        <p className="text-xs text-slate-500 mt-1 max-w-xl">
                            Envío <strong>manual</strong>. Al registrarse, los nuevos docentes quedan pendientes.
                            Ingresa una vez al día y presiona <em>Enviar bienvenida a pendientes</em>.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={runMigration}
                            disabled={migrating}
                            data-testid="welcome-migrate-legacy-btn"
                            title="Marca a todos los usuarios existentes como 'ya enviado' para no reenviar a antiguos"
                        >
                            {migrating ? (
                                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            ) : (
                                <Database className="h-3.5 w-3.5 mr-1.5" />
                            )}
                            Migrar legacy
                        </Button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mt-4 border-b border-slate-200">
                    <button
                        type="button"
                        data-testid="welcome-tab-pending"
                        onClick={() => setTab('pending')}
                        className={cn(
                            'px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors',
                            tab === 'pending'
                                ? 'border-amber-700 text-amber-900'
                                : 'border-transparent text-slate-500 hover:text-slate-800'
                        )}
                    >
                        <Users className="h-3.5 w-3.5 inline-block mr-1" />
                        Pendientes
                        <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-900">
                            {pending.length}
                        </Badge>
                    </button>
                    <button
                        type="button"
                        data-testid="welcome-tab-sent"
                        onClick={() => setTab('sent')}
                        className={cn(
                            'px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors',
                            tab === 'sent'
                                ? 'border-emerald-700 text-emerald-900'
                                : 'border-transparent text-slate-500 hover:text-slate-800'
                        )}
                    >
                        <History className="h-3.5 w-3.5 inline-block mr-1" />
                        Enviadas
                        <Badge variant="secondary" className="ml-2 bg-emerald-100 text-emerald-900">
                            {sent.length}
                        </Badge>
                    </button>
                </div>
            </CardHeader>

            <CardContent className="pt-2">
                {tab === 'pending' && (
                    <div data-testid="welcome-pending-tab-content">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-xs text-slate-600">
                                {pending.length === 0 ? (
                                    <span className="text-emerald-700 font-medium">
                                        ✓ Sin pendientes — todos los nuevos registros estarán aquí al inscribirse.
                                    </span>
                                ) : (
                                    <>
                                        <strong>{pending.length}</strong> usuario(s) esperando bienvenida.
                                    </>
                                )}
                            </p>
                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openPreview()}
                                    data-testid="welcome-preview-btn"
                                    className="border-slate-300"
                                >
                                    <Eye className="h-3.5 w-3.5 mr-1.5" />
                                    Vista previa
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={sendBatch}
                                    disabled={sending || selectedIds.size === 0}
                                    className="bg-amber-700 hover:bg-amber-800 text-white"
                                    data-testid="welcome-send-batch-btn"
                                >
                                    {sending ? (
                                        <>
                                            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                            Enviando…
                                        </>
                                    ) : (
                                        <>
                                            <Send className="h-3.5 w-3.5 mr-1.5" />
                                            Enviar bienvenida a {selectedIds.size} seleccionado(s)
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>

                        {/* Barra de progreso */}
                        {sending && (
                            <div className="mb-3" data-testid="welcome-progress-bar">
                                <div className="h-2 w-full bg-amber-100 rounded overflow-hidden">
                                    <div
                                        className="h-full bg-amber-600 transition-all duration-300"
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                                <p className="text-[10px] text-slate-500 mt-1">Procesando uno por uno · {progress}%</p>
                            </div>
                        )}

                        {/* Resultados del último batch */}
                        {batchResults && (
                            <div
                                className="mb-4 rounded-lg border border-slate-200 bg-white p-3"
                                data-testid="welcome-batch-results"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs font-semibold text-slate-700">
                                        Resultado del último envío masivo
                                    </p>
                                    <div className="flex gap-2 text-[11px]">
                                        <Badge className="bg-emerald-100 text-emerald-900 hover:bg-emerald-100">
                                            ✓ {batchResults.ok}
                                        </Badge>
                                        <Badge className="bg-rose-100 text-rose-900 hover:bg-rose-100">
                                            ✗ {batchResults.fail}
                                        </Badge>
                                    </div>
                                </div>
                                <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
                                    {batchResults.results.map((r) => (
                                        <ResultRow key={r.id || r.email} r={r} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Tabla de pendientes */}
                        {loadingPending ? (
                            <div className="py-8 flex justify-center text-slate-500">
                                <Loader2 className="h-5 w-5 animate-spin" />
                            </div>
                        ) : pending.length === 0 ? (
                            <p className="text-xs text-slate-400 italic py-6 text-center">No hay nadie pendiente.</p>
                        ) : (
                            <div className="overflow-x-auto" data-testid="welcome-pending-table">
                                <div className="flex items-center justify-between gap-2 mb-2 px-1 text-xs">
                                    <div className="flex items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={toggleSelectAll}
                                            className="text-blue-700 hover:underline font-medium"
                                            data-testid="welcome-select-all-btn"
                                        >
                                            {selectedIds.size === pending.length && pending.length > 0
                                                ? 'Deseleccionar todos'
                                                : 'Seleccionar todos'}
                                        </button>
                                        <span className="text-slate-500">
                                            {selectedIds.size} de {pending.length} seleccionado(s)
                                        </span>
                                    </div>
                                    {selectedIds.size === 0 && (
                                        <span className="text-amber-700 text-[11px]">
                                            Marca los docentes a quienes enviar antes de presionar &ldquo;Enviar&rdquo;
                                        </span>
                                    )}
                                </div>
                                <table className="w-full text-xs">
                                    <thead className="bg-slate-50 text-slate-600">
                                        <tr>
                                            <th className="text-left px-3 py-2 font-semibold w-10">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.size === pending.length && pending.length > 0}
                                                    onChange={toggleSelectAll}
                                                    className="h-3.5 w-3.5 rounded border-slate-300"
                                                    data-testid="welcome-header-checkbox"
                                                />
                                            </th>
                                            <th className="text-left px-3 py-2 font-semibold">Nombre</th>
                                            <th className="text-left px-3 py-2 font-semibold">Email</th>
                                            <th className="text-left px-3 py-2 font-semibold">Inscripción</th>
                                            <th className="text-left px-3 py-2 font-semibold">Origen</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {pending.map((u) => {
                                            const isSelected = selectedIds.has(u.id);
                                            return (
                                                <tr
                                                    key={u.id}
                                                    className={isSelected ? 'bg-blue-50/60 hover:bg-blue-100/40' : 'hover:bg-amber-50/40'}
                                                    data-testid={`welcome-pending-row-${u.id}`}
                                                >
                                                    <td className="px-3 py-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => toggleOne(u.id)}
                                                            className="h-3.5 w-3.5 rounded border-slate-300"
                                                            data-testid={`welcome-checkbox-${u.id}`}
                                                        />
                                                    </td>
                                                    <td className="px-3 py-2 font-medium text-slate-900">
                                                        {u.nombre || <em className="text-slate-400">Sin nombre</em>}
                                                    </td>
                                                    <td className="px-3 py-2 text-slate-700">{u.email}</td>
                                                    <td className="px-3 py-2 text-slate-500">{formatDate(u.created_at)}</td>
                                                    <td className="px-3 py-2">
                                                        <Badge variant="secondary" className="text-[10px]">
                                                            {u.auth_provider === 'google' ? 'Google' : 'Email'}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {tab === 'sent' && (
                    <div data-testid="welcome-sent-tab-content">
                        <p className="text-xs text-slate-600 mb-3">
                            <strong>{sent.length}</strong> usuario(s) con bienvenida ya enviada (más recientes primero).
                        </p>
                        {loadingSent ? (
                            <div className="py-8 flex justify-center text-slate-500">
                                <Loader2 className="h-5 w-5 animate-spin" />
                            </div>
                        ) : sent.length === 0 ? (
                            <p className="text-xs text-slate-400 italic py-6 text-center">Aún no se ha enviado ninguna bienvenida.</p>
                        ) : (
                            <div className="overflow-x-auto" data-testid="welcome-sent-table">
                                <table className="w-full text-xs">
                                    <thead className="bg-slate-50 text-slate-600">
                                        <tr>
                                            <th className="text-left px-3 py-2 font-semibold">Nombre</th>
                                            <th className="text-left px-3 py-2 font-semibold">Email</th>
                                            <th className="text-left px-3 py-2 font-semibold">Inscripción</th>
                                            <th className="text-left px-3 py-2 font-semibold">Enviada</th>
                                            <th className="text-right px-3 py-2 font-semibold">Acción</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {sent.map((u) => (
                                            <tr key={u.id} className="hover:bg-emerald-50/40">
                                                <td className="px-3 py-2 font-medium text-slate-900">
                                                    {u.nombre || <em className="text-slate-400">Sin nombre</em>}
                                                </td>
                                                <td className="px-3 py-2 text-slate-700">{u.email}</td>
                                                <td className="px-3 py-2 text-slate-500">{formatDate(u.created_at)}</td>
                                                <td className="px-3 py-2 text-emerald-700">
                                                    <Clock className="h-3 w-3 inline-block mr-1" />
                                                    {formatDate(u.bienvenida_enviada_at)}
                                                </td>
                                                <td className="px-3 py-2 text-right">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => resendOne(u.id, u.email)}
                                                        disabled={resendingId === u.id}
                                                        data-testid={`welcome-resend-btn-${u.email}`}
                                                        className="h-7 text-[11px]"
                                                    >
                                                        {resendingId === u.id ? (
                                                            <Loader2 className="h-3 w-3 animate-spin" />
                                                        ) : (
                                                            <>
                                                                <RefreshCw className="h-3 w-3 mr-1" />
                                                                Reenviar
                                                            </>
                                                        )}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </CardContent>

            {/* ═══════════ Modal Vista Previa ═══════════ */}
            {previewOpen && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
                    onClick={() => setPreviewOpen(false)}
                    data-testid="welcome-preview-modal"
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden border border-slate-200"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header del modal */}
                        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                                <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">
                                    Vista previa · Idéntica al correo que recibirá el docente
                                </p>
                                <p className="text-sm font-semibold text-slate-900 truncate">
                                    Asunto: <span className="font-normal text-slate-700">Bienvenido(a) a Portafolio Digital Docente 2026</span>
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    placeholder="Probar con nombre…"
                                    value={previewName}
                                    onChange={(e) => setPreviewName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') openPreview(previewName, previewGenero);
                                    }}
                                    className="h-8 text-xs px-3 border border-slate-300 rounded-md w-44"
                                    data-testid="welcome-preview-name-input"
                                />
                                <select
                                    value={previewGenero}
                                    onChange={(e) => setPreviewGenero(e.target.value)}
                                    className="h-8 text-xs px-2 border border-slate-300 rounded-md bg-white"
                                    data-testid="welcome-preview-genero-select"
                                    title="Forma de tratamiento"
                                >
                                    <option value="">Auto (heurística)</option>
                                    <option value="femenino">Profesora</option>
                                    <option value="masculino">Profesor</option>
                                    <option value="sin_especificar">Prefiere no decir</option>
                                </select>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openPreview(previewName, previewGenero)}
                                    className="h-8 text-xs"
                                    data-testid="welcome-preview-refresh-btn"
                                >
                                    <RefreshCw className="h-3 w-3 mr-1" />
                                    Recargar
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        const w = window.open('', '_blank');
                                        if (w) {
                                            w.document.write(previewSrc);
                                            w.document.close();
                                        }
                                    }}
                                    className="h-8 text-xs"
                                    data-testid="welcome-preview-open-new-tab"
                                >
                                    <ExternalLink className="h-3 w-3 mr-1" />
                                    Nueva pestaña
                                </Button>
                                <button
                                    type="button"
                                    onClick={() => setPreviewOpen(false)}
                                    className="ml-1 h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-slate-200 text-slate-600"
                                    aria-label="Cerrar"
                                    data-testid="welcome-preview-close-btn"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        {/* Body — iframe con srcDoc para evitar problemas de auth */}
                        <div className="flex-1 bg-slate-100 overflow-hidden">
                            <iframe
                                title="Vista previa correo bienvenida"
                                srcDoc={previewSrc}
                                className="w-full h-full border-0"
                                style={{ minHeight: '70vh' }}
                                sandbox="allow-same-origin"
                                data-testid="welcome-preview-iframe"
                            />
                        </div>

                        {/* Footer del modal */}
                        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3 flex-wrap">
                            <p className="text-[11px] text-slate-500">
                                ✓ Este HTML es <strong>exactamente</strong> el mismo que se enviará vía Resend.
                            </p>
                            <Button
                                size="sm"
                                onClick={() => {
                                    setPreviewOpen(false);
                                    sendBatch();
                                }}
                                disabled={sending || pending.length === 0}
                                className="bg-amber-700 hover:bg-amber-800 text-white h-8 text-xs"
                                data-testid="welcome-preview-confirm-send-btn"
                            >
                                <Send className="h-3.5 w-3.5 mr-1" />
                                Confirmar y enviar a {pending.length} pendiente(s)
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
}
